import { useQuery } from 'react-query';

import _ from 'lodash';

import { baseApiUrl, fetcher, ServerErrorResponse, createParamString } from 'data_sources/constants';
import datetimeHelper from 'helpers/datetimeHelper';
import { generateRiskPoolMetrics } from './riskPoolMetricsGenerator';
import { useRiskPoolDemoData } from './useRiskPoolDemoData';

import type { AgencyModificationsSummary, AgencyModificationsApiResponse } from '../../_types/risk_pool';

interface QueryParams {
  startDate: string;
  endDate: string;
  search?: string;
  policyMods?: string;
}

// Transform API response to internal format with calculated percentages
// Note: noPercent is derived from yesPercent to ensure they sum to exactly 100%
const transformApiResponse = (apiData: AgencyModificationsApiResponse): AgencyModificationsSummary => {
  const total = apiData.total;
  const yesCount = apiData.yes;
  const noCount = apiData.no;
  const yesPercent = total > 0 ? Math.round((yesCount / total) * 100) : 0;
  const noPercent = total > 0 ? 100 - yesPercent : 0;

  return {
    yesCount,
    noCount,
    total,
    yesPercent,
    noPercent,
  };
};

// Apply filters to organizations data for mock data generation
const applyFilters = (data: any[], params: QueryParams): any[] => {
  let filteredData = [...data];

  // Apply search filter if search param is not empty
  if (!_.isEmpty(params.search)) {
    filteredData = _.filter(filteredData, (org) => org.name.toLowerCase().includes(params.search!.toLowerCase()));
  }

  // Apply policy modifications filter if policyMods param is not empty
  if (!_.isEmpty(params.policyMods)) {
    filteredData = _.filter(filteredData, (org) => {
      if (params.policyMods === 'YES') return org.modifiedPolicyCount > 0;
      if (params.policyMods === 'NO') return org.modifiedPolicyCount === 0;
      return true;
    });
  }

  return filteredData;
};

// Generate mock data from organizations for demo purposes
const generateMockData = (organizations: any[], params: QueryParams): AgencyModificationsSummary => {
  // Apply filters before calculating summary
  const filteredOrganizations = applyFilters(organizations, params);
  const total = filteredOrganizations.length;
  const yesCount = filteredOrganizations.filter((org) => org.modifiedPolicyCount > 0).length;
  const noCount = total - yesCount;
  const yesPercent = total > 0 ? Math.round((yesCount / total) * 100) : 0;
  const noPercent = total > 0 ? 100 - yesPercent : 0;

  return {
    yesCount,
    noCount,
    total,
    yesPercent,
    noPercent,
  };
};

export const getAgencyModifications = async (
  orgId: string,
  params: QueryParams,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<AgencyModificationsSummary> => {
  const url = `${baseApiUrl()}/activity-center/v1/organizations/${orgId}/risk-pool/policy/policy-modifications-summary`;

  const apiParams = {
    ...params,
  };

  const paramString = createParamString(apiParams);

  try {
    const apiData = await fetcher({
      method: 'get',
      url: `${url}?${paramString}`,
      options: { signal },
    });
    return transformApiResponse(apiData);
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export default function useAgencyModifications(
  orgId: string,
  params: QueryParams,
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  // Validate dates before enabling the query
  const areDatesValid = datetimeHelper.isValidDateString(params.startDate) && datetimeHelper.isValidDateString(params.endDate);
  const isDemoDataEnabled = useRiskPoolDemoData();

  return useQuery<AgencyModificationsSummary, Error>(
    ['agency_modifications_summary', orgId, params.startDate, params.endDate, params.search, params.policyMods],
    async ({ signal }): Promise<AgencyModificationsSummary> => {
      // If demo data is enabled, return mock data instead of calling API
      if (isDemoDataEnabled) {
        // Generate mock organizations and calculate from them
        const organizations = generateRiskPoolMetrics(orgId, params);
        return generateMockData(organizations, params);
      }
      return getAgencyModifications(orgId, params, signal, onError);
    },
    {
      ...options,
      enabled: !!orgId && areDatesValid,
      keepPreviousData: true,
      refetchOnWindowFocus: true,
      refetchInterval: isDemoDataEnabled ? false : 1000 * 60 * 2, // 2 minutes, disabled for demo data
    }
  );
}
